// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronRight, Compass } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { useAuthStore } from '@/store'
import { navState } from '@/lib/returnNav'
import { loadCoachSuggestions, type CoachPage, type CoachSuggestion } from '@/lib/regiecoach'

/**
 * Regiecoach-blok - één rustige begeleidende kaart per pagina.
 * Toont suggesties op basis van reeds ingevulde gegevens. Verschijnt niet
 * wanneer er geen relevante suggestie is, zodat de pagina rustig blijft.
 */
export function RegieCoachBlock({ page }: { page: CoachPage }) {
  const { userId } = useAuthStore()
  const navigate = useNavigate()
  const loc = useLocation()
  const [items, setItems] = useState<CoachSuggestion[]>([])

  useEffect(() => {
    if (!userId) return
    let alive = true
    loadCoachSuggestions(userId, page).then(s => { if (alive) setItems(s) }).catch(() => {})
    return () => { alive = false }
  }, [userId, page])

  if (!items.length) return null

  return (
    <Card className="border-sage-200 bg-sage-50/40 mt-2">
      <div className="flex items-center gap-2 mb-1">
        <Compass className="h-4 w-4 text-sage-600 flex-shrink-0" />
        <p className="text-sm font-semibold text-ink">Misschien wilt u ook nog kijken naar</p>
      </div>
      <p className="text-xs text-ink-4 mb-3 leading-relaxed">
        Suggesties op basis van wat u al heeft ingevuld. Geheel vrijblijvend.
      </p>
      <div className="flex flex-col gap-2">
        {items.map((it, i) => (
          <button
            key={i}
            type="button"
            onClick={() => navigate(it.route, navState(loc.pathname, 'vorige pagina'))}
            className="flex items-center gap-2.5 rounded-xl border border-sand-300 bg-white px-4 py-3 text-left hover:border-sage-300 hover:bg-sage-50 transition-all"
          >
            <span className="flex-1 text-sm text-ink">{it.label}</span>
            <ChevronRight className="h-4 w-4 text-ink-4 flex-shrink-0" />
          </button>
        ))}
      </div>
    </Card>
  )
}
